import { curry } from 'lodash/fp';

type SeparatorFunction<T> = (i: number) => T;
type Separator<T> = T | SeparatorFunction<T>;

const isFunctionSeparator = <T>(separator: Separator<T>): separator is (i: number) => T =>
    typeof separator === 'function';

const uncurriedIntersperse = <T, L>(separator: Separator<T>, list: L[]): (T | L)[] => {
    const output: (T | L)[] = [];
    let index = 0;
    const { length } = list;
    while (index < length) {
        if (index === length - 1) {
            output.push(list[index]);
        } else {
            const separatorComponent = isFunctionSeparator(separator) ? separator(index) : separator;
            output.push(list[index], separatorComponent);
        }
        index += 1;
    }
    return output;
};

interface CurriedIntersperse {
    <T, L>(separator: T | ((index: number) => T)): (list: Array<L>) => Array<T | L>;
    <T, L>(separator: T | ((index: number) => T), list: Array<L>): Array<T | L>;
    <T, L>(): (separator: T, list: Array<L>) => Array<T | L>;
}

export const intersperse: CurriedIntersperse = curry(uncurriedIntersperse);
